export type PetMissingRecordPrompt = {
  key: 'microchip' | 'insurance' | 'vet' | 'food' | 'documents' | 'reminders' | 'shared-care';
  title: string;
  body: string;
  path: '/profile' | '/pets' | '/documents' | '/health';
};

export type SharedPetCareStatusInput = {
  petName: string;
  peerName?: string | null;
  hasHouseholdShare: boolean;
  isSharedWithHousehold: boolean;
};

export type PetGuidanceSummaryInput = SharedPetCareStatusInput & {
  reminderCount?: number | null;
  dueReminderCount?: number | null;
  medicationCount?: number | null;
  hasFoodProfile?: boolean;
  hasMicrochipNumber?: boolean;
  hasInsuranceSummary?: boolean;
  hasVetPracticeSummary?: boolean;
  hasDocuments?: boolean;
  canPromptForDocuments?: boolean;
};

function safeCount(value: number | null | undefined) {
  return Math.max(0, Number(value || 0));
}

function joinTitles(prompts: PetMissingRecordPrompt[]) {
  return prompts.slice(0, 3).map((prompt) => prompt.title.toLowerCase()).join(', ');
}

export function buildPetSharedCareStatus(input: SharedPetCareStatusInput) {
  const peerLabel = input.peerName?.trim() || 'your linked household';

  if (!input.hasHouseholdShare) {
    return {
      headline: 'Only you can see this pet right now',
      body: 'Link one other adult in My Settings if you want the same pet record to show up for both of you.',
      actionLabel: 'Open My Settings',
    };
  }

  if (input.isSharedWithHousehold) {
    return {
      headline: `Shared with ${peerLabel}`,
      body: 'This pet record is visible to both adults. Each adult can keep their own Health reminders and medications from the same pet profile.',
      actionLabel: 'Keep shared',
    };
  }

  return {
    headline: `Only you can see ${input.petName} right now`,
    body: `Share this pet with ${peerLabel} if both adults need the same pet record in Adulting.`,
    actionLabel: `Share with ${peerLabel}`,
  };
}

export function buildPetMissingRecordPrompts(input: PetGuidanceSummaryInput): PetMissingRecordPrompt[] {
  const prompts: PetMissingRecordPrompt[] = [];
  const reminderCount = safeCount(input.reminderCount);
  const medicationCount = safeCount(input.medicationCount);

  if (!input.isSharedWithHousehold && input.hasHouseholdShare) {
    const peerLabel = input.peerName?.trim() || 'your linked household';
    prompts.push({
      key: 'shared-care',
      title: 'Share the pet record',
      body: `Turn on shared care if ${peerLabel} also needs this pet to show up in Adulting.`,
      path: '/pets',
    });
  }

  if (!input.hasMicrochipNumber) {
    prompts.push({
      key: 'microchip',
      title: 'Add the microchip number',
      body: 'This is one of the most useful pet details to have ready quickly.',
      path: '/profile',
    });
  }

  if (!input.hasInsuranceSummary) {
    prompts.push({
      key: 'insurance',
      title: 'Save the insurance summary',
      body: 'A short policy note makes claim/admin moments much calmer later.',
      path: '/profile',
    });
  }

  if (!input.hasVetPracticeSummary) {
    prompts.push({
      key: 'vet',
      title: 'Save the vet practice details',
      body: 'Keeping the practice name or contact summary handy helps when something changes quickly.',
      path: '/profile',
    });
  }

  if (!input.hasFoodProfile) {
    prompts.push({
      key: 'food',
      title: 'Add the current food',
      body: 'One food profile lets Adulting warn before you run out instead of leaving it to memory.',
      path: '/pets',
    });
  }

  if (input.canPromptForDocuments && !input.hasDocuments) {
    prompts.push({
      key: 'documents',
      title: 'Save the key paperwork',
      body: 'Insurance, adoption, vaccination, or vet paperwork can live in the Pets folder in Documents.',
      path: '/documents',
    });
  }

  if (reminderCount === 0 && medicationCount === 0) {
    prompts.push({
      key: 'reminders',
      title: 'Add one recurring care reminder',
      body: 'One routine reminder is enough to stop regular care from becoming another thing to remember alone.',
      path: '/health',
    });
  }

  return prompts;
}

export function buildPetGuidanceSummary(input: PetGuidanceSummaryInput) {
  const dueReminderCount = safeCount(input.dueReminderCount);
  const reminderCount = safeCount(input.reminderCount);
  const medicationCount = safeCount(input.medicationCount);
  const missingPrompts = buildPetMissingRecordPrompts(input);
  const peerLabel = input.peerName?.trim() || 'your linked household';

  if (dueReminderCount > 0) {
    return {
      headline: `${input.petName} already has care due`,
      body: `Start in My Health first. ${dueReminderCount} pet reminder${dueReminderCount === 1 ? ' is' : 's are'} already due there.`,
    };
  }

  if (missingPrompts.length > 0) {
    return {
      headline: `${input.petName} is set up, but a few basics would help`,
      body: `The next best fixes are ${joinTitles(missingPrompts)}.`,
    };
  }

  if (input.isSharedWithHousehold && input.hasHouseholdShare) {
    return {
      headline: `${input.petName} is ready for shared care`,
      body: `This pet is visible to both adults through the shared household trail with ${peerLabel}.`,
    };
  }

  if (reminderCount > 0 || medicationCount > 0) {
    return {
      headline: `${input.petName} already has an active care trail`,
      body: 'My Health is already holding the ongoing reminders or medications for this pet.',
    };
  }

  return {
    headline: `${input.petName} is in good shape`,
    body: 'The core pet record is already in place, and Adulting can build from here without turning this into another job.',
  };
}
